import { Button } from "@ancestors/components/Button";
import { useLocalStorage } from "@ancestors/components/useLocalStorage";
import { cn } from "@ancestors/components/utils";
import { getUserLocale } from "get-user-locale";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";
import { suspend } from "suspend-react";
import type { ScriptKey, ScriptLang } from "virtual:script";
import { scripts } from "virtual:script";
import type { ScriptContextValue } from "./scriptContext";
import { scriptContext } from "./scriptContext";

const LOCALSTORAGE_LANG_KEY = "ancestors:language";

const availableLanguages = Object.keys(scripts);
const defaultLanguage = availableLanguages.includes("en") ? "en" : availableLanguages[0];

function detectLanguage() {
  const locale = getUserLocale();
  const preferredLanguage = import.meta?.env?.VITE_PREFERRED_LANGUAGE
    ? import.meta.env.VITE_PREFERRED_LANGUAGE
    : locale.split("-")[0]; // Locale can be smth like `en-US`
  const language = availableLanguages.includes(preferredLanguage)
    ? preferredLanguage
    : defaultLanguage;

  return language as ScriptLang;
}

interface ScriptProviderProps {
  children: ReactNode;
  override?: Record<string, string>;
  disableLanguagePicker?: boolean;
}

const EMPTY = {};

export function ScriptProvider({
  children,
  override: dynamicContent = EMPTY,
  disableLanguagePicker,
}: ScriptProviderProps) {
  // Selected language
  const [language, setLanguage] = useLocalStorage(LOCALSTORAGE_LANG_KEY, detectLanguage());

  // Guard against invalid languages that might be stored in localStorage
  if (!availableLanguages.includes(language)) {
    setLanguage(detectLanguage());
  }

  // Language picker
  const [languagePickerState, setLanguagePickerState] = useState<"open" | "closed">("closed");
  const [languagePickerVariant, setLanguagePickerVariant] = useState<"subtle" | "labeled">(
    "subtle",
  );

  const toggleLanguagePicker = useCallback(() => {
    setLanguagePickerState((s) => (s === "open" ? "closed" : "open"));
  }, []);

  const handleSelectLanguage = useCallback(
    (language: ScriptLang) => {
      setLanguage(language);
      setLanguagePickerState("closed");
    },
    [setLanguage],
  );

  // Lazily load the script content for the selected language
  const script = suspend(async () => {
    const script = await scripts[language].load();

    let dynamicHtmlContent: Record<string, string> = {};

    if (Object.keys(dynamicContent).length) {
      const { markdownToHTML } = await import("~/lib/markdownToHtml");

      dynamicHtmlContent = Object.fromEntries(
        await Promise.all(
          Object.entries(dynamicContent).map(async ([key, textContent]) => [
            key,
            await markdownToHTML(textContent),
          ]),
        ),
      );
    }

    return {
      text: (key: ScriptKey) => dynamicContent[key] ?? script.text(key),
      html: (key: ScriptKey) => dynamicHtmlContent[key] ?? script.html(key),
      key: (key: ScriptKey) => (key in dynamicContent ? key : script.key(key)),
    };
  }, [language]);

  const contextValue = useMemo<ScriptContextValue>(
    () => ({ script, language, setLanguagePickerVariant }),
    [language, script],
  );

  return (
    <scriptContext.Provider value={contextValue}>
      {disableLanguagePicker ? (
        children
      ) : (
        <div className="grid h-full w-full *:col-span-full *:row-span-full">
          {children}

          {availableLanguages.length > 1 && (
            <div
              className={cn(
                "z-10 flex flex-col bg-transparent p-2 transition-colors",
                languagePickerState === "closed" && "pointer-events-none",
                languagePickerState === "open" && "bg-background",
              )}
            >
              <Button
                variant="ghost"
                size={languagePickerVariant === "labeled" ? "sm" : "icon"}
                className="pointer-events-auto flex gap-2 self-end"
                onClick={toggleLanguagePicker}
              >
                {languagePickerState === "closed" ? <LucideLanguagesIcon /> : <LucideXIcon />}
                {languagePickerVariant === "labeled" && <span>Select language</span>}
              </Button>

              <div
                className={cn(
                  "grid flex-1 content-center items-center justify-center gap-y-2 transition-opacity",
                  languagePickerState === "closed" ? "opacity-0" : "opacity-100",
                )}
              >
                <p className="text-lg font-medium">Select your language:</p>
                {Object.entries(scripts).map(([languageTag, { languageLabel }]) => (
                  <Button
                    key={languageTag}
                    variant={language === languageTag ? "default" : "secondary"}
                    onClick={() => handleSelectLanguage(languageTag as ScriptLang)}
                  >
                    {languageLabel}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </scriptContext.Provider>
  );
}

function LucideLanguagesIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" {...props}>
      {/* Icon from Lucide by Lucide Contributors - https://github.com/lucide-icons/lucide/blob/main/LICENSE */}
      <path
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="m5 8l6 6m-7 0l6-6l2-3M2 5h12M7 2h1m14 20l-5-10l-5 10m2-4h6"
      />
    </svg>
  );
}

function LucideXIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" {...props}>
      {/* Icon from Lucide by Lucide Contributors - https://github.com/lucide-icons/lucide/blob/main/LICENSE */}
      <path
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M18 6L6 18M6 6l12 12"
      />
    </svg>
  );
}
